<script lang="ts" setup>
import TravelOrderStatus from "@/components/status/TravelOrderStatus.vue";
import BrokerTeamIcon from "@/components/icons/BrokerTeamIcon.vue";
import UnderTrackingIcon from "@/components/icons/UnderTrackingIcon.vue";
import type { TravelOrderDto, TripOverviewRoute } from "@/api/trips/dto/trip.dto";
import type { OrderDto } from "@/api/orders/dto/order.dto";
import { useGoogleMap } from "@/composables/useGoogleMap";
import { computed, onMounted, ref } from "vue";
import type { DriverDto } from "@/api/users/dto/driver.dto";
import { API } from "@/api/api";
import { useError } from "@/composables/useError";
import TruckDriverInfo from "@/views/orders/components/view/TruckDriverInfo.vue";

const api = new API();

const props = defineProps<{
  travelOrder: TravelOrderDto;
  order: OrderDto;
  routes: TripOverviewRoute[];
}>();

const { getPlaceUrl } = useGoogleMap();
const { showAPIError } = useError();

const driver = ref<DriverDto | null>(null);

async function loadDriver() {
  if (!relatedRoute.value?.driverId) {
    return;
  }

  try {
    const response = await api.users.findDriverById(relatedRoute.value.driverId);

    if (!response.data || !response.success) {
      showAPIError(response);
      return;
    }

    driver.value = response.data;
  } catch (error) {
    console.error(error);
  }
}

const relatedRoute = computed(() => {
  const result = props.routes.find((route) => {
    return route.travelOrders.some((to) => to.id === props.travelOrder.id);
  });

  return result;
});

const relatedTruck = computed(() => {
  return relatedRoute.value?.truck;
});

onMounted(async () => {
  await loadDriver();
});
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between flex-wrap mb-2">
      <div>
        <router-link
          :to="{ name: 'trip-view', params: { id: order.tripId } }"
          class="mr-3 text-decoration-none text-primary"
          data-qa="travel_order-number"
          target="_blank"
        >
          TO #{{ travelOrder.id }}
        </router-link>
        <travel-order-status :travel-order="travelOrder"></travel-order-status>
      </div>
      <div>Driver rate: {{ travelOrder.rate }}$</div>
    </div>
    <div class="d-flex align-center justify-space-between mb-1">
      <div>
        <v-icon class="mr-2" color="primary" icon="mdi-truck-outline" size="16"></v-icon>
        <router-link
          v-if="relatedTruck"
          :to="{ name: 'truck-view', params: { id: relatedTruck.id } }"
          class="text-decoration-none text-primary mr-2 font-size-12"
          >Truck #{{ relatedTruck.number }}
        </router-link>
      </div>
      <div class="d-flex align-center">
        <broker-team-icon :team-for-broker="order.teamForBroker" />
        <under-tracking-icon :under-tracking="order.underTracking" color="success" icon="mdi-map-marker-outline" />
      </div>
    </div>

    <div v-if="relatedTruck" class="d-flex align-center mb-1">
      {{ relatedTruck.currentLocation?.fullAddress }}
      <a
        v-if="relatedTruck.currentLocation?.coordinates"
        :href="getPlaceUrl(relatedTruck?.currentLocation.coordinates)"
        target="_blank"
      >
        <v-tooltip location="top">
          <template #activator="{ props: tooltipProps }">
            <v-icon
              class="text-green text--green-accent-2 ml-2"
              icon="mdi-map-marker-radius-outline"
              size="16"
              v-bind="tooltipProps"
            ></v-icon>
          </template>
          Current location
        </v-tooltip>
      </a>
    </div>

    <truck-driver-info v-if="driver" :driver="driver"></truck-driver-info>
  </div>
</template>

<style lang="scss" scoped></style>
